"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RawFirestore = void 0;
class RawFirestore {
    constructor(db) {
        this.db = db;
        // construct
    }
    appFirestore() {
        return this.db;
    }
    rootCollection(name) {
        return this.appFirestore().collection(name);
    }
    cheatCodesCollection() {
        return this.rootCollection('cheat-codes');
    }
    cheatCodeDoc(code) {
        return this.cheatCodesCollection().doc(code);
    }
    getCheatCodesSnapshot() {
        return this.cheatCodesCollection().get();
    }
    setCheatCode(code, data) {
        return this.cheatCodeDoc(code).set(data);
    }
    async clearCheatCodes() {
        const collection = await this.getCheatCodesSnapshot();
        return Promise.all(collection.docs.map((doc) => doc.ref.delete()));
    }
    // Refs: games
    gamesCollection() {
        return this.rootCollection('games');
    }
    gameDoc(gameCode) {
        return this.gamesCollection().doc(gameCode);
    }
    playersCollection(gameCode) {
        return this.gameDoc(gameCode).collection('users');
    }
    playerDoc(gameCode, playerEmail) {
        return this.playersCollection(gameCode).doc(playerEmail);
    }
    gameModsCollection(gameCode) {
        return this.gameDoc(gameCode).collection('mods');
    }
    limitedEducationGameModDoc(gameCode) {
        return this.gameModsCollection(gameCode).doc('limitedEducation');
    }
    limitedEducationGameModRoundsCollection(gameCode) {
        return this.limitedEducationGameModDoc(gameCode).collection('rounds');
    }
    limitedEducationGameModRoundDoc(gameCode, round) {
        return this.limitedEducationGameModRoundsCollection(gameCode).doc(round.toString());
    }
    // Refs: games - [game] - reflection-answers
    reflectionAnswersCollection(gameCode) {
        return this.gameDoc(gameCode).collection('reflection-answers');
    }
    reflectionAnswersDoc(gameCode, email) {
        return this.reflectionAnswersCollection(gameCode).doc(email);
    }
    // Refs: constants
    constantsCollection() {
        return this.rootCollection('constants');
    }
    createCharacterConstantsDoc() {
        return this.constantsCollection().doc('create_character');
    }
    educationConstantsDoc() {
        return this.constantsCollection().doc('education');
    }
    gameConstantsDoc() {
        return this.constantsCollection().doc('game');
    }
    workConstantsDoc() {
        return this.constantsCollection().doc('work-constants');
    }
    shopConstantsDoc() {
        return this.constantsCollection().doc('shop');
    }
    reflectionQuestionsDoc() {
        return this.constantsCollection().doc('reflection-questions');
    }
    // Refs: versions
    versionsCollection() {
        return this.rootCollection('versions');
    }
    versionDoc(version) {
        return this.versionsCollection().doc(version);
    }
    lifeCardsCollection(version) {
        return this.versionDoc(version).collection('life-cards');
    }
    lifeCardsDoc(version) {
        return this.lifeCardsCollection(version).doc('life-cards');
    }
    allSkillsCollection(version) {
        return this.versionDoc(version).collection('all-skills');
    }
    allSkillsDoc(version) {
        return this.allSkillsCollection(version).doc('all-skills');
    }
    versionConstantsCollection(version) {
        return this.versionDoc(version).collection('constants');
    }
    valuesDoc(version) {
        return this.versionConstantsCollection(version).doc('values');
    }
    companyValuesDoc(version) {
        return this.versionConstantsCollection(version).doc('company-values');
    }
    educationCollection(version) {
        return this.versionDoc(version).collection('education');
    }
    educationLevelsDoc(version) {
        return this.educationCollection(version).doc('levels');
    }
    educationQuestionsCollection(version) {
        return this.educationLevelsDoc(version).collection('questions');
    }
    educationQuestionsDoc(version, part) {
        return this.educationQuestionsCollection(version).doc(part);
    }
    questionsAmountCollection(version) {
        return this.educationLevelsDoc(version).collection('questionsAmount');
    }
    versionSectorsCollection(version) {
        return this.versionDoc(version).collection('sectors');
    }
    sectorDoc(version, sector) {
        return this.versionSectorsCollection(version).doc(sector);
    }
    sectorEducationCollection(version, sector) {
        return this.sectorDoc(version, sector).collection('education');
    }
    sectorSkillsDoc(version, sector) {
        return this.sectorEducationCollection(version, sector).doc('skills');
    }
    sectorWorkCollection(version, sector) {
        return this.sectorDoc(version, sector).collection('work');
    }
    careerTreeDoc(version, sector) {
        return this.sectorWorkCollection(version, sector).doc('careerTree');
    }
    companiesDoc(version, sector) {
        return this.sectorWorkCollection(version, sector).doc('companies');
    }
    highestPositionsDoc(version, sector) {
        return this.sectorWorkCollection(version, sector).doc('highestPositions');
    }
    jobsDoc(version, sector) {
        return this.sectorWorkCollection(version, sector).doc('jobs');
    }
    jobCollection(version, sector, job) {
        return this.jobsDoc(version, sector).collection(job);
    }
    jobDescriptionDoc(params) {
        const { version, sector, job } = params;
        return this.jobCollection(version, sector, job).doc('description');
    }
    jobQuestionsDoc(params) {
        const { version, sector, job } = params;
        return this.jobCollection(version, sector, job).doc('questions');
    }
    jobSalaryDoc(params) {
        const { version, sector, job } = params;
        return this.jobCollection(version, sector, job).doc('salary');
    }
    jobSkillsDoc(params) {
        const { version, sector, job } = params;
        return this.jobCollection(version, sector, job).doc('skills');
    }
    questionsAmountStudyDoc(version, study) {
        return this.questionsAmountCollection(version).doc(study);
    }
    setCheatCodes(cheatCodes) {
        return Promise.all(cheatCodes.map(([code, codeData]) => {
            return this.setCheatCode(code, codeData);
        }));
    }
    updateWorkConstants(data) {
        return this.workConstantsDoc().update(data);
    }
    updateEducationConstants(data) {
        return this.educationConstantsDoc().update(data);
    }
    updateCreateCharacterConstants(data) {
        return this.createCharacterConstantsDoc().update(data);
    }
    updateGameConstants(data) {
        return this.gameConstantsDoc().update(data);
    }
    updateShopConstants(data) {
        return this.shopConstantsDoc().update(data);
    }
    async getReflectionQuestions() {
        const doc = await this.reflectionQuestionsDoc().get();
        return doc.data();
    }
    async getVersion(version) {
        const doc = await this.versionDoc(version).get();
        return doc.data();
    }
    updateVersion(version, data) {
        return this.versionDoc(version).update(data);
    }
    listenVersion(version, onNext) {
        const off = this.versionDoc(version).onSnapshot(onNext);
        return off;
    }
    updateLifeCards(version, data) {
        return this.lifeCardsDoc(version).update(data);
    }
    async getAllSkills(version) {
        const doc = await this.allSkillsDoc(version).get();
        return doc.data();
    }
    updateAllSkills(version, data) {
        return this.allSkillsDoc(version).update(data);
    }
    async getValues(version) {
        const doc = await this.valuesDoc(version).get();
        return doc.data();
    }
    setValues(version, data) {
        return this.valuesDoc(version).set(data);
    }
    async getCompanyValues(version) {
        const doc = await this.companyValuesDoc(version).get();
        return doc.data();
    }
    setCompanyValues(version, data) {
        return this.companyValuesDoc(version).set(data);
    }
    async getEducationLevels(version) {
        const doc = await this.educationLevelsDoc(version).get();
        return doc.data();
    }
    async clearEducationQuestions(version) {
        const snapshot = await this.educationQuestionsCollection(version).get();
        return Promise.all(snapshot.docs.map((doc) => doc.ref.delete()));
    }
    async getEducationQuestions(version, part) {
        const doc = await this.educationQuestionsDoc(version, part).get();
        return doc.data();
    }
    setEducationQuestions(version, part, data) {
        return this.educationQuestionsDoc(version, part).set(data);
    }
    async clearQuestionsAmounts(version) {
        const snapshot = await this.questionsAmountCollection(version).get();
        return Promise.all(snapshot.docs.map((doc) => doc.ref.delete()));
    }
    async getQuestionsAmount(version, study) {
        const doc = await this.questionsAmountStudyDoc(version, study).get();
        return doc.data();
    }
    setQuestionsAmount(version, study, data) {
        return this.questionsAmountStudyDoc(version, study).set(data);
    }
    updateEducationLevels(version, data) {
        return this.educationLevelsDoc(version).update(data);
    }
    async getSectorSkills(version, sector) {
        const doc = await this.sectorSkillsDoc(version, sector).get();
        return doc.data();
    }
    setSectorSkills(version, sector, data) {
        return this.sectorSkillsDoc(version, sector).set(data);
    }
    async getCareerTree(version, sector) {
        const doc = await this.careerTreeDoc(version, sector).get();
        return doc.data();
    }
    setCareerTree(version, sector, data) {
        return this.careerTreeDoc(version, sector).set(data);
    }
    async getCompanies(version, sector) {
        const doc = await this.companiesDoc(version, sector).get();
        return doc.data();
    }
    setCompanies(version, sector, data) {
        return this.companiesDoc(version, sector).set(data);
    }
    async getHighestPositions(version, sector) {
        const doc = await this.highestPositionsDoc(version, sector).get();
        return doc.data();
    }
    setHighestPositions(version, sector, data) {
        return this.highestPositionsDoc(version, sector).set(data);
    }
    async getJobDescription(params) {
        const doc = await this.jobDescriptionDoc(params).get();
        return doc.data();
    }
    setJobDescription(params, data) {
        return this.jobDescriptionDoc(params).set(data);
    }
    async getJobQuestions(params) {
        const doc = await this.jobQuestionsDoc(params).get();
        return doc.data();
    }
    setJobQuestions(params, data) {
        return this.jobQuestionsDoc(params).set(data);
    }
    async getJobSalary(params) {
        const doc = await this.jobSalaryDoc(params).get();
        return doc.data();
    }
    setJobSalary(params, data) {
        return this.jobSalaryDoc(params).set(data);
    }
    async getJobSkills(params) {
        const doc = await this.jobSkillsDoc(params).get();
        return doc.data();
    }
    setJobSkills(params, data) {
        return this.jobSkillsDoc(params).set(data);
    }
    // Data: games
    async getGame(gameCode) {
        const doc = await this.gameDoc(gameCode).get();
        return doc.data();
    }
    onGame(gameCode, onNext) {
        const off = this.gameDoc(gameCode).onSnapshot(onNext);
        return off;
    }
    async getPlayers(gameCode) {
        const collection = await this.playersCollection(gameCode).get();
        return collection.docs.map((doc) => doc.data());
    }
    async getPlayer(gameCode, playerEmail) {
        const doc = await this.playerDoc(gameCode, playerEmail).get();
        return doc.data();
    }
    mergePlayer(gameCode, playerEmail, data) {
        return this.playerDoc(gameCode, playerEmail).set(data, { merge: true });
    }
    onLimitedEducationGameModRound(params) {
        const { gameCode, round, onNext } = params;
        const off = this.limitedEducationGameModRoundDoc(gameCode, round).onSnapshot(onNext);
        return off;
    }
    // games - [game] - reflection-answers
    async getReflectionAnswers(gameCode, email) {
        const doc = await this.reflectionAnswersDoc(gameCode, email).get();
        return doc.data();
    }
}
exports.RawFirestore = RawFirestore;
